import ErrorMessage from '@/components/ErrorMessage'
import Loading from '@/components/Loading'
import LoginSignup from '@/components/LoginSignup'
import { Unauthenticated, Authenticated } from '@/utils/auth/auth'
import { getCurrentUser } from '@/utils/auth/UserProvider'
import { useCurrentUserOrgs } from '@/utils/hooks/orgs'
import { OrgUser } from '@/models/Users'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Tabs,
  Tab,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { UUID } from 'crypto'
import { useState, useEffect, useRef } from 'react'
import { useFocusManagement } from '@/utils/hooks/useFocusManagement'
import { loadStripe, Stripe } from '@stripe/stripe-js'
import MembershipUpgrade from '@/components/MembershipUpgrade'
import InvoicePaymentConfirmation from '@/components/InvoicePaymentConfirmation'
import SubscriptionManagement from '@/components/SubscriptionManagement'
import { updateSubscription } from '@/api/subscriptionsAPI'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { usePageTitle } from '@/utils/hooks/usePageTitle'
import { fetchOrgAccountDetails } from '@/api/orgsAPI'
import ResetPassword from '@/components/ResetPassword'
import LinkAccountModal from '@/components/LinkAccount'
import {
  BillingInformation,
  PaymentHistory,
  BillingDashboardLive,
} from '@/components/billing'
import {
  fetchBillingDashboard,
  updateBillingInfo,
  BillingInfo,
} from '@/api/billingAPI'

const stripeKey = import.meta.env.VITE_PUBLIC_STRIPE_KEY

// Tab URL mapping
const TAB_NAMES = ['billing', 'security'] as const
type TabName = (typeof TAB_NAMES)[number]

const getTabIndex = (tabName: string | null): number => {
  if (!tabName) return 0
  const index = TAB_NAMES.indexOf(tabName as TabName)
  return index >= 0 ? index : 0
}

const getTabName = (index: number): TabName => {
  return TAB_NAMES[index] || 'billing'
}

// Create a TabPanel component
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `account-tab-${index}`,
    'aria-controls': `account-tabpanel-${index}`,
  }
}

interface AccountPageProps {
  embedded?: boolean
}

export default function AccountPage({ embedded = false }: AccountPageProps) {
  const pageTitle = embedded ? 'Organisation Profile' : 'Manage Account'
  usePageTitle(pageTitle, embedded ? { preserveOnUnmount: true } : undefined)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, token, isLoading: userLoading, orgs } = getCurrentUser()

  // URL-based tab management
  const currentTab = searchParams.get('tab')
  const tabValue = embedded ? 0 : getTabIndex(currentTab)
  const focusRef = useFocusManagement()
  const {
    orgData,
    isLoading: orgsLoading,
    isError,
    refetch: refetchOrgs,
  } = useCurrentUserOrgs(token as string, user, orgs as UUID[])
  const [organisation, setOrganisation] = useState(orgData?.[0])

  useEffect(() => {
    if (user && orgData && orgData.length > 0) {
      if (!user.selected_org_uuid) {
        setOrganisation(orgData[0])
        return
      } else {
        const selectedOrg = orgData.find(
          (org) => org.uuid === user.selected_org_uuid,
        )
        if (selectedOrg) {
          setOrganisation(selectedOrg || orgData[0])
        }
      }
    }
  }, [orgData, organisation, user])

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null)
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const messageAlertRef = useRef<HTMLDivElement>(null)

  // A new error/success should replace whichever one is currently showing,
  // not sit alongside it — otherwise a failed status refresh right after a
  // successful pause would render both messages at once.
  const showError = (message: string) => {
    setSuccess(null)
    setError(message)
  }
  const showSuccess = (message: string) => {
    setError(null)
    setSuccess(message)
  }

  // Switching organisation makes any prior message stale.
  useEffect(() => {
    setError(null)
    setSuccess(null)
  }, [organisation?.uuid])

  // The alert renders below potentially long billing sections — scroll it
  // into view so a new message isn't silently off-screen.
  useEffect(() => {
    if ((error || success) && messageAlertRef.current) {
      messageAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [error, success])
  const [billingEmail, setBillingEmail] = useState<string | null>(null)
  const [billingFrequency, setBillingFrequency] = useState<string | undefined>(
    undefined,
  )
  const [loadingBillingEmail, setLoadingBillingEmail] = useState<boolean>(false)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false)
  const [showLinkAccountModal, setShowLinkAccountModal] = useState(false)
  const [billingInfoV2, setBillingInfoV2] = useState<BillingInfo | null>(null)
  const [paymentHistoryKey, setPaymentHistoryKey] = useState(0)
  const [billingSyncStatus, setBillingSyncStatus] = useState<
    string | null | undefined
  >(undefined)

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (embedded) return
    const tabName = getTabName(newValue)
    navigate(`/account?tab=${tabName}`, { replace: true })
  }

  useEffect(() => {
    if (user && stripeKey) {
      setStripePromise(loadStripe(stripeKey))
    } else if (user && !stripeKey) {
      // If no Stripe key is configured, set a resolved promise to null
      // This allows the page to render without Stripe functionality
      setStripePromise(Promise.resolve(null))
    }
  }, [user])

  // fetch billing email for invoice confirmation on success page
  useEffect(() => {
    const isSuccessPage = location.pathname === '/account/success'

    if (
      isSuccessPage &&
      organisation?.payment_type?.toLowerCase() === 'invoice' &&
      organisation.uuid &&
      token
    ) {
      setLoadingBillingEmail(true)
      fetchOrgAccountDetails(token, organisation.uuid)
        .then((result) => {
          if ('accountDetails' in result) {
            if (result.accountDetails.billing_email) {
              setBillingEmail(result.accountDetails.billing_email)
            }
            if (result.accountDetails.billing_frequency) {
              setBillingFrequency(result.accountDetails.billing_frequency)
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching account details')
        })
        .finally(() => {
          setLoadingBillingEmail(false)
        })
    }
  }, [location.pathname, organisation, token])

  // fetch billing info for billing tab
  useEffect(() => {
    if (
      tabValue === 0 &&
      organisation?.uuid &&
      token &&
      !(organisation.payment_type === null && organisation.is_child_of)
    ) {
      fetchBillingDashboard(organisation.uuid, token)
        .then((data) => {
          setBillingInfoV2(data.billing_info)
        })
        .catch((err) => {
          console.error('Error fetching billing info')
        })
    }
  }, [tabValue, organisation?.uuid, token])

  async function handleManageSubscription() {
    if (!user) return
    setLoading(true)

    try {
      const data = await updateSubscription(organisation, token as string)
      if (data.url) {
        window.location.href = data.url
      } else {
        showError('Failed to load the customer portal.')
        setLoading(false)
      }
    } catch {
      showError('Failed to load the customer portal.')
      setLoading(false)
    }
  }

  async function handleUpdateBillingInfo(
    updates: Partial<BillingInfo>,
    surfaceError = true,
  ): Promise<BillingInfo | null> {
    if (!organisation?.uuid || !token) return null

    try {
      const updatedInfo = await updateBillingInfo(
        organisation.uuid,
        token,
        updates,
      )
      setBillingInfoV2(updatedInfo)
      return updatedInfo
    } catch (err) {
      if (surfaceError) {
        showError(
          err instanceof Error
            ? err.message
            : 'Failed to update billing information',
        )
      }
      return null
    }
  }

  function renderBillingContent() {
    if (!organisation) return null
    const isPayerChild =
      organisation.payment_type === null && organisation.is_child_of

    if (isPayerChild) {
      return (
        <Box sx={{ maxWidth: 900, mx: 'auto', mt: 5 }}>
          <Alert severity="info">
            Billing for {organisation.name} is managed through{' '}
            {organisation.parent_org_name || 'its parent organisation'}.
            Contact an administrator of the parent organisation to make billing
            changes.
          </Alert>
        </Box>
      )
    }

    return (
      <>
        {(organisation.membership?.toLowerCase() || 'free') === 'free' ? (
          <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mt: 5,
              }}
            >
              <Typography variant="h5" align="center">
                {organisation.name}
              </Typography>
              <Typography variant="h6" sx={{ mb: 4 }}>
                {organisation.membership_until &&
                new Date(organisation.membership_until) < new Date()
                  ? `Subscription expired on ${new Date(
                      organisation.membership_until,
                    ).toLocaleDateString('en-NZ', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })}`
                  : 'Currently on the Free Plan'}
              </Typography>
            </Box>

            {stripeKey ? (
              <MembershipUpgrade
                orgUUID={organisation.uuid}
                orgName={organisation.name}
                token={token}
                stripePromise={stripePromise!}
                tier={organisation.tier ?? undefined}
                onError={showError}
                onSuccess={async () => {
                  const refreshedOrgs = await refetchOrgs()
                  if (refreshedOrgs && refreshedOrgs.length > 0) {
                    setOrganisation(refreshedOrgs[0])
                  }
                }}
                userEmail={user?.email}
                userFirstName={user?.first_name || undefined}
                userLastName={user?.last_name || undefined}
              />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mt: 4,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ textAlign: 'center', color: '#666666' }}
                >
                  Payment processing is not currently configured. Please contact
                  support to upgrade your plan.
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <>
            <BillingDashboardLive
              orgUUID={organisation.uuid}
              token={token as string}
              onManageSubscription={
                organisation.payment_type?.toLowerCase() === 'stripe' &&
                organisation.subscription_status?.toLowerCase() !== 'cancelled'
                  ? handleManageSubscription
                  : undefined
              }
              pauseResumeEnabled={organisation?.pause_resume_enabled}
              stripePromise={stripePromise ?? undefined}
              onRefresh={() => {
                setPaymentHistoryKey((k) => k + 1)
                refetchOrgs()
              }}
              onSyncStatusChange={setBillingSyncStatus}
              orgName={organisation.name}
              userEmail={user?.email}
              userFirstName={user?.first_name || undefined}
              userLastName={user?.last_name || undefined}
              billingEmail={billingInfoV2?.billing_email}
              onUpdateBillingInfo={(updates) =>
                handleUpdateBillingInfo(updates, false)
              }
            >
              {organisation?.pause_resume_enabled && (
                <Box sx={{ mb: 3 }}>
                  <SubscriptionManagement
                    orgUUID={organisation.uuid}
                    token={token as string}
                    onError={showError}
                    onSuccess={(message) => {
                      showSuccess(message)
                      refetchOrgs()
                    }}
                  />
                </Box>
              )}
            </BillingDashboardLive>
            <Box sx={{ maxWidth: 900, mx: 'auto' }}>
              <PaymentHistory
                key={paymentHistoryKey}
                orgUUID={organisation.uuid}
                token={token as string}
                syncStatus={billingSyncStatus}
              />
              <BillingInformation
                billingInfo={billingInfoV2}
                onUpdate={handleUpdateBillingInfo}
              />
            </Box>
          </>
        )}
        {error && (
          <Alert ref={messageAlertRef} severity="error" sx={{ mt: 2, width: 400, mx: 'auto' }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert ref={messageAlertRef} severity="success" sx={{ mt: 2, width: 400, mx: 'auto' }}>
            {success}
          </Alert>
        )}
      </>
    )
  }

  // Check if we need to wait for billing email to load on success page
  const isSuccessPage = location.pathname === '/account/success'
  const needsBillingEmail =
    isSuccessPage && organisation?.payment_type?.toLowerCase() === 'invoice'

  if (
    orgsLoading ||
    userLoading ||
    !stripePromise ||
    loading ||
    (needsBillingEmail && loadingBillingEmail)
  ) {
    return <Loading />
  }

  if (isError) {
    return <ErrorMessage />
  }

  if (
    isSuccessPage &&
    organisation?.payment_type?.toLowerCase() === 'invoice' &&
    billingEmail
  ) {
    return (
      <Authenticated>
        <Box
          sx={{
            margin: '0 auto',
            marginTop: isMobile ? 2 : 5,
            py: isMobile ? 2 : 5,
            px: isMobile ? 2 : 15,
            maxWidth: 800,
          }}
        >
          <InvoicePaymentConfirmation
            organisationName={organisation.name}
            billingEmail={billingEmail}
            tier={organisation.tier ?? 4}
            billingFrequency={billingFrequency}
          />
        </Box>
      </Authenticated>
    )
  }

  return (
    <>
      <Unauthenticated>
        <LoginSignup />
      </Unauthenticated>
      <Authenticated>
        <>
          {organisation ? (
            <>
              <Box
                sx={{
                  mx: 'auto',
                  mt: embedded ? 0 : isMobile ? 2 : 5,
                  width: '100%',
                  py: embedded ? 0 : isMobile ? 2 : 5,
                  pb: embedded ? 0 : isMobile ? 20 : 5,
                  px: embedded ? 0 : isMobile ? 2 : 15,
                }}
              >
                {showResetPasswordModal && (
                  <ResetPassword
                    email={user?.email || ''}
                    showResetPasswordModal={showResetPasswordModal}
                    setShowResetPasswordModal={setShowResetPasswordModal}
                    token={token}
                  />
                )}
                {showLinkAccountModal && (
                  <LinkAccountModal
                    showLinkAccountModal={showLinkAccountModal}
                    setShowLinkAccountModal={setShowLinkAccountModal}
                    token={token}
                  />
                )}
                {!embedded && (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      width: '100%',
                    }}
                  >
                    <Typography
                      variant="h1"
                      component="h1"
                      ref={focusRef}
                      tabIndex={-1}
                      sx={{
                        mb: 3,
                        textAlign: 'left',
                        fontSize: '34px',
                        '&:focus': {
                          outline: 'none',
                        },
                      }}
                    >
                      Manage Account
                    </Typography>
                  </Box>
                )}

                {!embedded && (
                  <>
                    {/* Tabs */}
                    <Box
                      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                      <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        aria-label="account tabs"
                      >
                        <Tab disableRipple label="Billing" {...a11yProps(0)} />
                        <Tab
                          disableRipple
                          label="Security & Preferences"
                          {...a11yProps(1)}
                        />
                      </Tabs>
                    </Box>

                    {/* Billing Tab - Live Dashboard */}
                    <TabPanel value={tabValue} index={0}>
                      {renderBillingContent()}
                    </TabPanel>

                    {/* Settings Tab */}
                    <TabPanel value={tabValue} index={1}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Divider sx={{ mt: 8, mb: 2, width: '80%' }}>
                          Login & Security
                        </Divider>

                        {/* only visable to users with email/password logins, not google login */}
                        {user &&
                          user.auth0_id &&
                          user.auth0_id.startsWith('auth0|') && (
                            <Button
                              variant="text"
                              disableRipple
                              sx={{
                                textTransform: 'none',
                                borderRadius: 0,
                                '&:focus': {
                                  backgroundColor: '#e8e8e8',
                                },
                              }}
                              onClick={() => setShowResetPasswordModal(true)}
                            >
                              Reset my password{' '}
                            </Button>
                          )}

                        <Button
                          variant="text"
                          disableRipple
                          sx={{
                            textTransform: 'none',
                            '&:focus': {
                              backgroundColor: '#e8e8e8',
                            },
                          }}
                          onClick={() => setShowLinkAccountModal(true)}
                        >
                          Manage my Login Methods
                        </Button>
                      </Box>
                    </TabPanel>
                  </>
                )}

                {embedded && renderBillingContent()}
              </Box>
            </>
          ) : null}
        </>
      </Authenticated>
    </>
  )
}
